import React from 'react';
import { FormattedMessage } from 'react-intl';
import { INTEGRATION_KEY } from '../../state/integration/Constants';

class QuoteEvents {
    static eventTypes() {
        const colors = {
            "primary": '#1546db',
            "secondary": '#6b6a74',
            "success": '#6dc00c',
            "danger": '#d40a0a',
            "warning": '#e6ad0e',
            "info": '#17cdc1',
            "light": '#f2f2f2',
            "dark": '#4d4d4d',
        }
        return [
            {
                id: 'quote-created',
                icon: 'plus-circle',
                color: colors.primary,
                desc: <FormattedMessage
                    id="quote.analytics.calendar.event_types.quote_created.description"
                    defaultMessage="Proposal created"
                />
            },
            {
                id: 'quote-proposed',
                icon: 'comment-dollar',
                color: colors.primary,
                desc: <FormattedMessage
                    id="quote.analytics.calendar.event_types.quote_proposed.description"
                    defaultMessage="Proposal sent"
                />
            },
            {
                id: 'quote-pre-approved',
                icon: 'check',
                color: colors.warning,
                desc: <FormattedMessage
                    id="quote.analytics.calendar.event_types.quote_pre_approved.description"
                    defaultMessage="Proposal accepted"
                />
            },
            {
                id: 'quote-approved',
                icon: 'check-circle',
                color: colors.success,
                desc: <FormattedMessage
                    id="quote.analytics.calendar.event_types.quote_approved.description"
                    defaultMessage="Proposal approved"
                />
            },
            {
                id: 'quote-pre-declined',
                icon: 'times',
                color: colors.warning,
                desc: <FormattedMessage
                    id="quote.analytics.calendar.event_types.quote_pre_declined.description"
                    defaultMessage="Proposal refused"
                />
            },
            {
                id: 'quote-declined',
                icon: 'times-circle',
                color: colors.danger,
                desc: <FormattedMessage
                    id="quote.analytics.calendar.event_types.quote_declined.description"
                    defaultMessage="Proposal declined"
                />
            },
            {
                id: 'email-delivered',
                icon: 'envelope',
                color: colors.info,
                desc: <FormattedMessage
                    id="quote.analytics.calendar.event_types.email_delivered.description"
                    defaultMessage="Email sent"
                />
            },
            {
                id: 'email-opened',
                icon: 'envelope-open',
                color: colors.info,
                desc: <FormattedMessage
                    id="quote.analytics.calendar.event_types.email_opened.description"
                    defaultMessage="Email opened"
                />
            },
            {
                id: 'email-clicked',
                icon: 'hand-point-up',
                color: colors.info,
                desc: <FormattedMessage
                    id="quote.analytics.calendar.event_types.email_clicked.description"
                    defaultMessage="Proposal opened"
                />
            },
            {
                id: 'proposal-view',
                icon: 'eye',
                color: colors.info,
                desc: <FormattedMessage
                    id="quote.analytics.calendar.event_types.proposal-view.description"
                    defaultMessage="Proposal viewed"
                />
            },
        ]
    }

    static getEventComponent(event, quote, proposal, invoice, order, onUpdate, stockDocument, accountStatement) {
        try {
            const Section = require("./events/" + event.type + ".component")
            if (!Section) {
                return ''
            }
            return <Section.default
                id={'event-' + event.id}
                event={event}
                object={event.object}
                quote={quote}
                order={order}
                depositPayableObject={quote || order}
                proposal={proposal}
                invoice={invoice}
                onUpdate={onUpdate}
                stockDocument={stockDocument}
                accountStatement={accountStatement}
            />
        } catch (e) {
            return <strong>{event.type}</strong>
        }
    }

    static formatQuoteEvents(emails, visits, quote, order, invoice, project, invoices, signatures, returns, calendarEvent, stockDocument, accountStatement, proposalEmails) {
        let events = []
        for (let i in emails) {
            const email = emails[i]
            for (let y in email.attributes.events) {
                const event = email.attributes.events[y]
                if (['opened'].includes(event.attributes.eventType)) {
                    continue
                }
                events.push({
                    type: 'email-' + event.attributes.eventType,
                    date: event.attributes.createdAt,
                    id: event.id,
                    object: email,
                })
            }
        }
        if (visits) {
            let publicVisits = visits.filter(v => v.attributes.userId == "")
            for (let i in publicVisits) {
                const visit = publicVisits[i]
                events.push({
                    type: 'proposal-view',
                    date: visit.attributes.createdAt,
                    id: visit.id,
                    object: visit,
                })
            }
        }

        if (signatures) {
            for (let i in signatures) {
                const signature = signatures[i]
                events.push({
                    type: 'signature-created',
                    date: signature.attributes.createdAt,
                    id: signature.id,
                    object: signature,
                })
            }
        }
        if (invoice) {
            events.push({
                type: 'invoice-created',
                date: invoice.attributes.createdAt,
                id: invoice.id + '-created',
                object: invoice,
            })
            if (invoice.attributes.sentAt) {
                events.push({
                    type: 'invoice-sent',
                    date: invoice.attributes.sentAt,
                    id: invoice.id + '-sent',
                    object: invoice,
                })
            }
            for (let i in invoice.attributes.paymentTransactions) {
                const transaction = invoice.attributes.paymentTransactions[i]
                events.push({
                    type: 'invoice-payment-received',
                    date: transaction.attributes.createdAt,
                    id: transaction.id + '-invoice-payment-received',
                    object: transaction,
                })
            }
            if (invoice.attributes.fullyPaidAt &&
                invoice.attributes.status === "fully-paid" &&
                parseFloat(invoice.attributes.balance) === 0) {
                events.push({
                    type: 'invoice-fully-paid',
                    date: invoice.attributes.fullyPaidAt,
                    id: invoice.id + '-fully-paid',
                    object: invoice,
                })
            }
            if (invoice.attributes.voidedAt) {
                events.push({
                    type: 'invoice-voided',
                    date: invoice.attributes.voidedAt,
                    id: invoice.id + '-voided',
                    object: invoice,
                })
            }
            if (invoice.attributes.importedAt && invoice.attributes.importedFrom) {
                events.push({
                    type: 'invoice-imported',
                    date: invoice.attributes.importedAt,
                    id: invoice.id + '-imported',
                    object: invoice,
                })
            }
            if (invoice.attributes.externalLinks.length > 0) {
                for (let index in invoice.attributes.externalLinks) {
                    const link = invoice.attributes.externalLinks[index]
                    const type = 'invoice-exported-' + link.attributes.integrationKey
                    events.push({
                        type: type,
                        date: link.attributes.createdAt,
                        id: link.id,
                        object: link,
                    })
                }
            }
            if (invoice.attributes.exports.length > 0) {
                for (let invoiceExport of invoice.attributes.exports.filter(e => e.attributes.status === 'error')) {
                    const exp = invoiceExport
                    events.push({
                        type: 'export-error',
                        date: exp.attributes.updatedAt,
                        id: exp.id,
                        object: exp,
                    })
                }
            }
            const oneTimeTotal = invoice.attributes.totals.length > 0 ? invoice.attributes.totals[0] : null
            if (oneTimeTotal) {
                for (let feeCalc of oneTimeTotal.attributes.feeCalculations) {
                    const lateFeeCalc = feeCalc.attributes.lateFeeCalculation
                    if (lateFeeCalc) {
                        for (let link of lateFeeCalc.attributes.externalLinks) {
                            if ([INTEGRATION_KEY.LIGHTSPEED_RETAIL].includes(link.attributes.integrationKey)) {
                                const type = 'invoice-exported-' + link.attributes.integrationKey
                                events.push({
                                    type: type,
                                    date: link.attributes.createdAt,
                                    id: link.id,
                                    object: link,
                                })
                            }
                        }
                    }
                }
            }
        }
        if (quote) {
            events.push({
                type: 'quote-created',
                date: quote.attributes.createdAt,
                id: quote.id + '-created',
                object: quote,
            })
            if (quote.attributes.proposedAt) {
                events.push({
                    type: 'quote-proposed',
                    date: quote.attributes.proposedAt,
                    id: quote.id + '-proposed',
                    object: quote,
                })
            }
            if (quote.attributes.preApprovedAt) {
                events.push({
                    type: 'quote-pre-approved',
                    date: quote.attributes.preApprovedAt,
                    id: quote.id + '-pre-approved',
                    object: quote,
                })
            }
            if (quote.attributes.approvedAt) {
                events.push({
                    type: 'quote-approved',
                    date: quote.attributes.approvedAt,
                    id: quote.id + '-approved',
                    object: quote,
                })
            }
            if (quote.attributes.preDeclinedAt) {
                events.push({
                    type: 'quote-pre-declined',
                    date: quote.attributes.preDeclinedAt,
                    id: quote.id + '-pre-declined',
                    object: quote,
                })
            }
            if (quote.attributes.declinedAt) {
                events.push({
                    type: 'quote-declined',
                    date: quote.attributes.declinedAt,
                    id: quote.id + '-declined',
                    object: quote,
                })
            }
            if (quote.attributes.orderedAt) {
                events.push({
                    type: 'order-from-quote-created',
                    date: quote.attributes.orderedAt,
                    id: quote.id + '-ordered',
                    object: quote,
                })
            }
            if (quote.attributes.externalLinks.length > 0) {
                for (let index in quote.attributes.externalLinks) {
                    const link = quote.attributes.externalLinks[index]
                    const type = 'quote-exported-' + link.attributes.integrationKey
                    events.push({
                        type: type,
                        date: link.attributes.createdAt,
                        id: link.id,
                        object: link,
                    })
                }
            }
            if (quote.attributes.externalOrders.length > 0) {
                for (let index in quote.attributes.externalOrders) {
                    const order = quote.attributes.externalOrders[index]
                    events.push({
                        type: 'order-from-quote-created-' + order.attributes.integrationKey,
                        date: order.attributes.createdAt,
                        id: order.id,
                        object: order,
                    })
                }
            }
            if (quote.attributes.exports.length > 0) {
                for (let quoteExport of quote.attributes.exports.filter(e => e.attributes.status === 'error')) {
                    const exp = quoteExport
                    events.push({
                        type: 'export-error',
                        date: exp.attributes.updatedAt,
                        id: exp.id,
                        object: exp,
                    })
                }
            }
            for (let i in quote.attributes.paymentTransactions) {
                const transaction = quote.attributes.paymentTransactions[i]
                events.push({
                    type: 'quote-payment-received',
                    date: transaction.attributes.createdAt,
                    id: transaction.id,
                    object: transaction,
                })
            }
        }
        if (order) {
            events.push({
                type: 'order-created',
                date: order.attributes.createdAt,
                id: order.id + '-created',
                object: order,
            })
            if (order.attributes.customStatusDates.length > 0) {
                for (let index in order.attributes.customStatusDates) {
                    const date = order.attributes.customStatusDates[index]
                    events.push({
                        type: 'order-status-changed',
                        date: date.attributes.changedAt,
                        id: date.attributes.customStatusId,
                        object: date,
                    })
                }
            }
            for (let i in order.attributes.paymentTransactions) {
                const transaction = order.attributes.paymentTransactions[i]
                events.push({
                    type: 'quote-payment-received',
                    date: transaction.attributes.createdAt,
                    id: transaction.id,
                    object: transaction,
                })
            }

            if (order.attributes.externalLinks.length > 0) {
                const externalLink = order.attributes.externalLinks[order.attributes.externalLinks.length - 1]
                if (externalLink.attributes.version > 0 && externalLink.attributes.integrationKey === INTEGRATION_KEY.LIGHTSPEED_RETAIL) {
                    events.push({
                        type: 'order-reservation-exported-' + externalLink.attributes.integrationKey,
                        date: externalLink.attributes.createdAt,
                        id: externalLink.id,
                        object: externalLink,
                        order: order,
                    })
                }
            }

            if (order.attributes.externalLinksIntegrations.length > 0) {
                for (let index in order.attributes.externalLinksIntegrations) {
                    const link = order.attributes.externalLinksIntegrations[index]
                    const type = 'order-exported-' + link.attributes.integrationKey
                    events.push({
                        type: type,
                        date: link.attributes.createdAt,
                        id: link.id,
                        object: link,
                    })
                }
            }
        }


        if (project) {
            if (project.attributes.internalNotes && project.attributes.internalNotes.length > 0) {
                for (let index in project.attributes.internalNotes) {
                    const notes = project.attributes.internalNotes[index]
                    events.push({
                        type: 'internal-notes-changed',
                        date: notes.attributes.updatedAt,
                        id: notes.id,
                        object: notes,
                    })
                }

            }
        }

        if (calendarEvent) {
            events.push({
                type: 'calendar-event-created',
                date: calendarEvent.attributes.createdAt,
                id: calendarEvent.id + '-created',
                object: calendarEvent,
            })
        }

        if (invoices && invoices.length > 0) {
            for (let i in invoices) {
                events.push({
                    type: 'invoice-from-quote-created',
                    date: invoices[i].attributes.createdAt,
                    id: invoices[i].id + '-invoice-from-quote-created',
                    object: invoices[i],
                })
            }
        }
        for (let r of returns) {
            events.push({
                type: 'return-from-invoice-created',
                date: r.attributes.createdAt,
                id: r.id + '-return-from-invoice-created',
                object: r,
                invoice,
            })
        }

        if (stockDocument) {
            events.push({
                type: 'stock-document-created',
                date: stockDocument.attributes.createdAt,
                id: stockDocument.id + '-created',
                object: stockDocument,
            })
            if (stockDocument.attributes.sentAt) {
                events.push({
                    type: 'stock-document-sent',
                    date: stockDocument.attributes.sentAt,
                    id: stockDocument.id + '-sent',
                    object: stockDocument,
                })
            }
            if (stockDocument.attributes.completedAt) {
                events.push({
                    type: 'stock-document-completed',
                    date: stockDocument.attributes.completedAt,
                    id: stockDocument.id + '-completed',
                    object: stockDocument,
                })
            }
            if (stockDocument.attributes.movementHistory) {
                stockDocument.attributes.movementHistory.forEach(
                    (movement, index) => {
                        events.push({
                            type: 'stock-document-movement',
                            date: movement.attributes.movementDate,
                            id: index + '-movement',
                            object: movement,
                        })
                    }
                )
            }
        }
        if (accountStatement) {
            events.push({
                type: 'account-statement-created',
                date: accountStatement.dto.attributes.createdAt,
                id: accountStatement.id + '-created',
                object: accountStatement,
            })
            if (Array.isArray(proposalEmails) && proposalEmails.length > 0) {
                // Last email to get the oldest sent at
                const email = proposalEmails.slice(-1)[0]
                events.push({
                    type: 'account-statement-sent',
                    date: email.dto.attributes.sentAt || email.dto.attributes.createdAt,
                    id: accountStatement.id + '-sent',
                    object: accountStatement,
                })
            }
            if (accountStatement.dto.attributes.payment) {
                events.push({
                    type: 'account-statement-payment-received',
                    date: accountStatement.dto.attributes.payment.attributes.capturedAt,
                    id: accountStatement.id + '-payment-received',
                    object: accountStatement,
                })
            }
        }
        events = events.map(event => {
            if (event.date) {
                event.formattedDate = new Date(event.date)
            }
            return event
        })
        events.sort((a, b) => {
            return (a.formattedDate > b.formattedDate) ? -1 : (a.formattedDate < b.formattedDate) ? 1 : eventTypeOrderOnEquals(a, b)
        })
        return events
    }

}

function eventTypeOrderOnEquals(a, b) {
    if (a.type === 'invoice-fully-paid') {
        return -1
    }

    return 0
}

export default QuoteEvents





// WEBPACK FOOTER //
// ./src/quote/analytics/events.jsx